import { esc } from '../dom';
import { t } from '../i18n';

/**
 * Diálogo de confirmação do backoffice, no lugar do `confirm()`/`prompt()` do navegador.
 * Um por vez; fecha com Esc, clique fora ou Cancelar (resolve como cancelado).
 */
export interface DialogOptions {
  title: string;
  /** Emoji ou símbolo no selo do topo. */
  icon?: string;
  /** Cor do selo e do botão principal. */
  tone?: 'go' | 'primary' | 'danger';
  /** HTML já escapado por quem chama. */
  body?: string;
  confirmLabel: string;
  cancelLabel?: string;
}

export interface PromptOptions extends DialogOptions {
  label: string;
  placeholder?: string;
  /** Mínimo de letras para liberar o botão principal. */
  minLength?: number;
  /** Sugestões que preenchem o campo com um clique. */
  suggestions?: string[];
}

export function confirmDialog(opts: DialogOptions): Promise<boolean> {
  return open(opts).then(v => v !== null);
}

export function promptDialog(opts: PromptOptions): Promise<string | null> {
  return open(opts, true);
}

let closeCurrent: (() => void) | null = null;

function open(opts: DialogOptions | PromptOptions, withInput = false): Promise<string | null> {
  closeCurrent?.();
  const p = opts as PromptOptions;
  const tone = opts.tone ?? 'primary';
  const min = p.minLength ?? 0;

  const root = document.createElement('div');
  root.className = 'dlg-backdrop';
  root.innerHTML = `
    <div class="dlg dlg-${tone}" role="dialog" aria-modal="true" aria-labelledby="dlg-title">
      <button type="button" class="dlg-x" data-dlg="cancel" aria-label="${esc(t('common.cancel'))}">✕</button>
      <div class="dlg-head">
        ${opts.icon ? `<span class="dlg-icon">${esc(opts.icon)}</span>` : ''}
        <h3 id="dlg-title">${esc(opts.title)}</h3>
      </div>
      ${opts.body ? `<div class="dlg-body">${opts.body}</div>` : ''}
      ${withInput ? `
        <label class="dlg-field">
          <span>${esc(p.label)}</span>
          <textarea id="dlg-input" rows="3" maxlength="300" placeholder="${esc(p.placeholder ?? '')}"></textarea>
        </label>
        ${p.suggestions?.length ? `<div class="dlg-sugs">${p.suggestions.map(s => `<button type="button" class="dlg-sug" data-sug="${esc(s)}">${esc(s)}</button>`).join('')}</div>` : ''}` : ''}
      <div class="dlg-actions">
        <button type="button" class="btn" data-dlg="cancel">${esc(opts.cancelLabel ?? t('common.cancel'))}</button>
        <button type="button" class="btn ${tone === 'go' ? 'btn-go' : tone === 'danger' ? 'btn-danger' : 'btn-primary'}" data-dlg="ok">${esc(opts.confirmLabel)}</button>
      </div>
    </div>`;
  document.body.appendChild(root);
  const prevFocus = document.activeElement as HTMLElement | null;
  const okBtn = root.querySelector<HTMLButtonElement>('[data-dlg="ok"]')!;
  const input = root.querySelector<HTMLTextAreaElement>('#dlg-input');

  const sync = () => { if (input) okBtn.disabled = input.value.trim().length < min; };
  sync();
  requestAnimationFrame(() => root.classList.add('on'));
  // foco: no campo quando há, senão no botão principal
  setTimeout(() => (input ?? okBtn).focus(), 30);

  return new Promise<string | null>(resolve => {
    const finish = (value: string | null) => {
      document.removeEventListener('keydown', onKey, true);
      closeCurrent = null;
      root.remove();
      prevFocus?.focus?.();
      resolve(value);
    };
    const confirm = () => {
      if (okBtn.disabled) return;
      finish(input ? input.value.trim() : '');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); finish(null); }
      // Enter confirma; no campo de texto, Ctrl/⌘+Enter (Enter sozinho quebra linha)
      else if (e.key === 'Enter' && (!input || e.ctrlKey || e.metaKey)) { e.preventDefault(); confirm(); }
    };
    closeCurrent = () => finish(null);
    document.addEventListener('keydown', onKey, true);
    input?.addEventListener('input', sync);
    root.addEventListener('click', e => {
      const el = e.target as HTMLElement;
      if (el === root) return finish(null);
      const act = el.closest<HTMLElement>('[data-dlg]')?.dataset.dlg;
      if (act === 'cancel') return finish(null);
      if (act === 'ok') return confirm();
      const sug = el.closest<HTMLElement>('[data-sug]');
      if (sug && input) { input.value = sug.dataset.sug ?? ''; sync(); input.focus(); }
    });
  });
}
